import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames/bind';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

// Hooks
import { useInvitesTerms } from './hooks/useInvitesTerms';
import { useScrollToTopOnMount } from 'utils/hooks/useScrollToTopOnMount';

// Components
import { DataBlocks, I18n } from 'components';
import { Text } from '@beef/ui-kit';
import declint from 'utils/declint';
import { FamilyTabs } from '../../components/Tabs';
import { AddNumberButton } from '../../components/AddNumberButton';
import { SkeletonWrapper } from '../../components/SkeletonWrapper';
import { ComponentsRouter } from '../ComponentsRouter';
import { PriceContent } from '../../components/Price';

// Utils
import {
  fetchManageFamilyData,
  setActiveStep,
  setPopupStatus,
} from '../../store/actions';
import { replaceSubstring } from '../../utils';
import { fetchFamilyData } from '../../../Home/actions';

// Constants
import { PAGE_PATHS, POPUP_NAMES } from '../../constants';

// Styles
import styles from './styles.pcss';

const cn = classNames.bind(styles);

const RootFamilyPage = ({ setPopupState }) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const {
    isLoading,
    isError,
    invites,
    count,
    content: { addButtonTooltipText },
    price,
    unit,
  } = useSelector((state) => state.family);

  useEffect(() => {
    dispatch(fetchManageFamilyData());
    dispatch(fetchFamilyData());
  }, []);

  useInvitesTerms({ invites, isLoading });

  const sharingDescription = replaceSubstring(addButtonTooltipText, '{{COUNT}}', invites.length);
  const changedDescription = declint(
    invites.length,
    ['номер', 'номера', 'номеров'],
  );
  const tooltipDescription = replaceSubstring(sharingDescription, '{{NUMERIC}}', changedDescription);

  const handleAddNumberClick = useCallback(() => {
    if (setPopupState) {
      setPopupState(true);
      return;
    }
    dispatch(setActiveStep(POPUP_NAMES.add));
    dispatch(setPopupStatus(true));
  }, []);

  useScrollToTopOnMount();

  const hasInvites = !!invites?.length;
  const isShowButtons = !isLoading && [PAGE_PATHS.information, PAGE_PATHS.terms].includes(pathname);
  const isShowPrice = hasInvites && [PAGE_PATHS.information].includes(pathname);
  const isNoPriceContainer = !price || [PAGE_PATHS.terms].includes(pathname);

  return (
    <DataBlocks.Container>
      <DataBlocks.Block>
        <main className={cn('container', {
          'container--no-price': isNoPriceContainer,
          'container--no-invites': !hasInvites,
        })}
        >
          <div className={cn('title')}>
            <Text
              isEllipsis
              size="size0-b"
              tagType="h2"
              testName="someName"
            >
              <I18n path="family.title" />
            </Text>
          </div>
          <div className={cn('balance')}>
            {isShowPrice && (<PriceContent unit={unit}>{price}</PriceContent>)}
          </div>
          <div className={cn('button')}>
            {(isShowButtons) && !isError && (
              <AddNumberButton
                isDisabled={invites.length >= count}
                tooltipText={tooltipDescription}
                onClick={handleAddNumberClick}
              />
            )}
          </div>

          {hasInvites && (
            <div className={cn('tabs')}>
              {!isError && !isLoading && (<FamilyTabs />)}
            </div>
          )}

          <div className={cn('content')}>
            <SkeletonWrapper isError={isError} isLoading={isLoading}>
              <ComponentsRouter />
            </SkeletonWrapper>
          </div>
        </main>
      </DataBlocks.Block>
    </DataBlocks.Container>
  );
};

RootFamilyPage.propTypes = {
  setPopupState: PropTypes.func,
};

export { RootFamilyPage };
